/**
 * SMS message formatting helpers
 */

/**
 * First token of a full name, or fallback.
 * @param {string|null|undefined} fullName
 * @param {string} [fallback]
 */
export function firstNameFromFullName(fullName, fallback = 'there') {
  const token = (fullName || '').trim().split(/\s+/)[0];
  return token || fallback;
}

/**
 * Returns the first name if it looks like a real name, otherwise returns the
 * fallback (default 'there'). Prevents "Hey .!" / "Hey seller!" artifacts from
 * poorly-imported contacts.
 * @param {string|null|undefined} name
 * @param {string} [fallback]
 */
export function safeFirstName(name, fallback = 'there') {
  if (!name) return fallback;
  const trimmed = String(name).trim();
  if (!trimmed) return fallback;
  const nonNames = new Set(['seller', 'buyer', 'client', 'unknown', 'n/a', 'na', 'test', 'user', 'contact']);
  if (nonNames.has(trimmed.toLowerCase())) return fallback;
  // Reject pure numbers / phone-like strings
  if (/^[\d\s\-().+]+$/.test(trimmed)) return fallback;
  // Reject email-prefix style
  if (trimmed.includes('@')) return fallback;
  // Reject single punctuation or single characters that aren't letters
  if (trimmed.length < 2 || /^[^a-zA-Z]+$/.test(trimmed)) return fallback;
  return trimmed;
}

/**
 * Returns true if the agent's full_name is usable as a sender identity
 * (non-empty, not a placeholder).
 * @param {string|null|undefined} fullName
 */
export function agentNameUsable(fullName) {
  if (!fullName) return false;
  const trimmed = String(fullName).trim();
  if (trimmed.length < 2) return false;
  const placeholders = new Set(['agent', 'your agent', 'user', 'admin', 'test']);
  if (placeholders.has(trimmed.toLowerCase())) return false;
  return true;
}

/**
 * Replace em/en dashes with comma punctuation — real texts rarely use dashes.
 */
export function sanitizeSmsDashes(text) {
  if (!text || typeof text !== 'string') return text;
  return text
    .replace(/\s*[—–]\s*/g, ', ')
    .replace(/,\s*,/g, ',')
    .replace(/[^\S\n]{2,}/g, ' ')
    .trim();
}

/**
 * First-contact SMS: intro paragraph, blank line, then touchpoint body.
 */
export function buildFirstContactMessage(intro, touchpointBody) {
  const body = sanitizeSmsDashes(touchpointBody);
  return `${intro}\n\n${body}`;
}

/** Default first-contact intro template (hardcoded fallback). */
export const DEFAULT_FIRST_CONTACT_INTRO_TEMPLATE =
  `Hey {first_name}, it's {agent_name}, I added a dedicated phone number for client communication, ` +
  `please make sure to save this number so you know it's me when I reach out! ` +
  `Just reply stop if you'd rather not get messages from me.`;

/**
 * Build the first-contact intro (sent once, as the standalone first message).
 * @param {string} clientName - The client's full name
 * @param {string} agentFullName - The agent's full name
 * @param {string|null} [template] - Optional custom template with {first_name} and {agent_name} placeholders.
 *   Null/undefined falls back to DEFAULT_FIRST_CONTACT_INTRO_TEMPLATE.
 */
export function buildFirstContactIntro(clientName, agentFullName, template) {
  const clientFirst = safeFirstName(firstNameFromFullName(clientName, ''), 'there');
  const agentName = (agentFullName || '').trim() || 'your agent';
  const tpl = (template && template.trim()) ? template.trim() : DEFAULT_FIRST_CONTACT_INTRO_TEMPLATE;
  return tpl
    .replace(/\{first_name\}/gi, clientFirst)
    .replace(/\{agent_name\}/gi, agentName);
}

/**
 * Strip trailing " - Agent Name" signature from stored touchpoint text.
 */
export function stripAgentSignature(message, agentFullName) {
  if (!message || !agentFullName) return message;
  const escaped = agentFullName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return message.replace(new RegExp(`\\s*-\\s*${escaped}\\s*$`, 'i'), '').trim();
}
